import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router";
import { Search, X } from "lucide-react";
import { PageTitle } from "@/components/brand";
import { BackLink, TopBar } from "@/components/navigation";
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from "@/components/ui/input-group";

/**
 * Spaces and searching for spaces (0085): two pages, `/spaces` and
 * `/spaces/search`, around one search box. The box is the same element on
 * both, so the tap that focuses it on Spaces — which is what raises a phone's
 * keyboard — leaves it focused on the search page: Safari lowers the keyboard
 * when the focused box goes, and this one never does. The bar above it is
 * Spaces' title on one and the way back on the other, the same height, so the
 * box does not move.
 */

/** How long typing pauses before the term is searched. */
export const SEARCH_PAUSE_MS = 300;

const SEARCH_PATH = "/spaces/search";

/** The search term in the address, as the box shows it. */
export function searchOf(search: string): string {
  return new URLSearchParams(search).get("q")?.trim() ?? "";
}

/** Marks the search page opened from Spaces' box, so its way back is Back. */
type Opened = { fromSpaces?: boolean } | null;

export default function SpacesFrame() {
  const location = useLocation();
  const navigate = useNavigate();
  const searching = location.pathname === SEARCH_PATH;
  const fromSpaces = (location.state as Opened)?.fromSpaces === true;

  return (
    <section className="space-y-6">
      <PageTitle of={searching ? ["Search", "Spaces"] : ["Spaces"]} />
      {searching ? (
        <>
          <h1 className="sr-only">Search spaces</h1>
          {/* Opened from Spaces, the way back is Back, so Spaces is not added
              again behind the search; reached by its address, it is a link. */}
          <BackLink
            to="/spaces"
            onClick={(event) => {
              if (!fromSpaces) return;
              event.preventDefault();
              void navigate(-1);
            }}
          >
            Spaces
          </BackLink>
        </>
      ) : (
        <TopBar>
          <h1 className="page-title">Spaces</h1>
        </TopBar>
      )}

      {/* Finding a space is one box under a section's label, as on a phone:
          no card, no button (0060). */}
      <div className="space-y-3">
        <h2 className="section-label">Find spaces</h2>
        <SearchBox searching={searching} fromSpaces={fromSpaces} query={searching ? searchOf(location.search) : ""} />
      </div>

      <Outlet />
    </section>
  );
}

/**
 * The box. On Spaces, tapping it opens the search page, the box staying
 * focused; tabbing to it does not, so the rest of Spaces stays in reach, and
 * typing there opens the page with the term. There, what is typed is the box's own until it is applied to the
 * address: after a pause in typing, at once with the Search key or the ×,
 * replacing the address rather than adding to it, so Back leaves the search.
 * `applied` is the term this box last put there, so an address that changes
 * any other way — Back, a link — is told from our own and puts its term in
 * the box.
 */
function SearchBox({ searching, fromSpaces, query }: { searching: boolean; fromSpaces: boolean; query: string }) {
  const navigate = useNavigate();
  const [text, setText] = useState(query);
  const applied = useRef(query);
  const input = useRef<HTMLInputElement>(null);
  // Where the page is now, read when a pause ends rather than when it began:
  // typed while the search page was still opening, a term replaces that page
  // once it is drawn, or overtakes its opening before — never adding a second
  // entry, and never replacing Spaces', so Back is Spaces either way.
  const here = useRef(searching);
  here.current = searching;

  // Opened from Spaces whenever this box opens it; a replacement keeps what
  // the page was opened with.
  const opened = here.current ? fromSpaces : true;
  const apply = (term: string) => {
    applied.current = term;
    const search = term ? `?${new URLSearchParams({ q: term })}` : "";
    void navigate({ pathname: SEARCH_PATH, search }, { replace: here.current, state: { fromSpaces: opened } });
  };

  // Leaving the search lets go of it: what was typed, a pause still running,
  // and the focus, so the keyboard goes down over Spaces and a tap there opens
  // the search again.
  useEffect(() => {
    if (searching) return;
    applied.current = "";
    setText("");
    input.current?.blur();
  }, [searching]);

  useEffect(() => {
    if (query !== applied.current) {
      applied.current = query;
      setText(query);
    }
  }, [query]);

  useEffect(() => {
    const term = text.trim();
    if (term === applied.current) return;
    const timer = setTimeout(() => apply(term), SEARCH_PAUSE_MS);
    return () => clearTimeout(timer);
    // Only the text starts a pause: `apply` reads a ref and the page.
  }, [text]);

  return (
    <form
      role="search"
      onSubmit={(event) => {
        event.preventDefault();
        apply(text.trim());
      }}
    >
      <InputGroup className="h-10">
        <InputGroupAddon>
          <Search aria-hidden="true" />
        </InputGroupAddon>
        <InputGroupInput
          ref={input}
          // Only on arriving at the search page by its address: on Spaces,
          // focusing the box opens the search.
          autoFocus={searching}
          name="q"
          value={text}
          onChange={(event) => setText(event.target.value)}
          onClick={() => {
            if (!searching) void navigate(SEARCH_PATH, { state: { fromSpaces: true } });
          }}
          placeholder="Search spaces"
          aria-label="Search spaces by name"
          inputMode="search"
          enterKeyHint="search"
          autoComplete="off"
        />
        {text && (
          <InputGroupAddon align="inline-end">
            <InputGroupButton
              type="button"
              size="icon-xs"
              aria-label="Clear the search"
              onClick={() => {
                setText("");
                apply("");
                input.current?.focus();
              }}
            >
              <X aria-hidden="true" />
            </InputGroupButton>
          </InputGroupAddon>
        )}
      </InputGroup>
    </form>
  );
}
